import { FocusMonitor } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { matSortAnimations, MatSortHeader, MatSortHeaderIntl } from '@angular/material/sort';
import { MatMultiSortDirective } from '@dice/app/components/organisms/mat-multi-sort/mat-multi-sort.directive';

/** Column definition associated with a `MatSortHeader`. */
interface C2MatSortHeaderColumnDef {
  name: string;
}

@Component({
  selector: '[mat-multi-sort-header]',
  exportAs: 'matMultiSortHeader',
  templateUrl: './mat-multi-sort-header.component.html',
  styleUrls: ['./mat-multi-sort-header.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    matSortAnimations.indicator,
    matSortAnimations.leftPointer,
    matSortAnimations.rightPointer,
    matSortAnimations.arrowOpacity,
    matSortAnimations.arrowPosition,
    matSortAnimations.allowChildren,
  ],
  standalone: false,
})
export class MatMultiSortHeaderComponent extends MatSortHeader {
  _intl: MatSortHeaderIntl;
  private changeDetectorRef: ChangeDetectorRef;
  _sort: MatMultiSortDirective;
  _columnDef: C2MatSortHeaderColumnDef;

  start = 'asc' as 'asc' | 'desc';

  @Input('mat-multi-sort-header') id: string;

  private indicatorHintVisible: boolean = false;

  private disableViewStateAnimation: boolean = false;

  private disableArrow: boolean = false;

  constructor() {
    const _intl = inject(MatSortHeaderIntl);
    const changeDetectorRef = inject(ChangeDetectorRef);
    const _sort = inject(MatMultiSortDirective, { optional: true });
    const _columnDef = inject<C2MatSortHeaderColumnDef>('C2_SORT_HEADER_COLUMN_DEF' as any, { optional: true });
    const _focusMonitor = inject(FocusMonitor);
    const _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    super(_intl, changeDetectorRef, _sort, _columnDef, _focusMonitor, _elementRef);

    this._intl = _intl;
    this.changeDetectorRef = changeDetectorRef;
    this._sort = _sort;
    this._columnDef = _columnDef;
  }

  // Allow a refresh of the arrows on multi sort header. It's required to initiate the sorting when refreshing the page
  public refreshArrow(): void {
    this.disableArrow = true;
    this.changeDetectorRef.detectChanges();
    this.disableArrow = false;
    this.changeDetectorRef.detectChanges();
  }

  @HostListener('mouseenter', ['true'])
  @HostListener('longpress', ['true'])
  @HostListener('mouseleave', ['false'])
  __setIndicatorHintVisible(visible: string | boolean): void {
    this.indicatorHintVisible = visible as boolean;
  }

  isSorted(): boolean {
    return this._sort.actives.findIndex((activeId) => activeId === this.id) > -1;
  }

  sortId(): number {
    return this._sort.actives.findIndex((activeId) => activeId === this.id) + 1;
  }

  @HostBinding('attr.aria-sort')
  _getAriaSortAttribute(): 'none' | 'ascending' | 'descending' {
    if (!this._isSorted()) {
      return null;
    }

    return this.getSortDirection() === 'asc' ? 'ascending' : 'descending';
  }

  renderArrow(): boolean {
    return !this._isDisabled() || (this._isSorted() && !this.disableArrow);
  }

  getSortDirection(): 'asc' | 'desc' | '' {
    const i = this._sort.actives.findIndex((activeIds) => activeIds === this.id);
    const direction = this._sort.directions[i];
    return this.isSorted() ? direction : this.start || this._sort.start;
  }

  get arrowOpacity(): number {
    if (this.isSorted()) {
      return 1;
    } else if (this.indicatorHintVisible) {
      return 0.54;
    }
    return 0;
  }

  getArrowDirectionState(): 'asc' | 'desc' | '' {
    return this.getSortDirection();
  }

  public enableAnimation(): void {
    this.disableViewStateAnimation = false;
  }

  public disableAnimation(): void {
    this.disableViewStateAnimation = true;
  }
}
